import React from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Row,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  CreditCardOutlined,
  CustomerServiceOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

type SubscriptionStatus =
  | 'ACTIVE'
  | 'INACTIVE'
  | 'PENDING'
  | 'FAILED'
  | 'CANCELED';

type PackageItem = {
  id: string;
  name: string;
  price: string;
  currency: string;
  period: string;
  features: string[];
  popular?: boolean;
};

const mockSubscription = {
  status: 'INACTIVE' as SubscriptionStatus,
  reasonCode: 'SUBSCRIPTION_INACTIVE',
  reasonMessage:
    'Aboneliğiniz aktif görünmüyor. Bu durum başarısız ödeme, iptal edilmiş paket veya tamamlanmamış abonelik adımlarından kaynaklanabilir.',
  currentPackageName: 'Profesyonel Paket',
  currentProductName: 'ISG Platform',
  price: 2499,
  currency: 'TRY',
  billingPeriod: 'Aylık',
  startedAt: '01.03.2026',
  nextBillingDate: '01.04.2026',
  canceledAt: '',
  paymentRetryAllowed: true,
  upgradeAllowed: true,
};

const mockPackages: PackageItem[] = [
  {
    id: 'starter',
    name: 'Başlangıç',
    price: '1500.00',
    currency: '₺',
    period: '/ay',
    features: [
      '10 Çalışan Limiti',
      'Temel Risk Analizi',
      'Doküman Yönetimi',
      'E-posta Bildirimleri',
    ],
    popular: false,
  },
  {
    id: 'enterprise',
    name: 'Kurumsal',
    price: 'Teklif Al',
    currency: '',
    period: '',
    features: [
      'Sınırsız Çalışan',
      'Tüm Modüller',
      'Size Özel Sunucu',
      'API Entegrasyonu',
      'Özel Müşteri Temsilcisi',
    ],
    popular: false,
  },
];

const statusConfigMap: Record<
  SubscriptionStatus,
  { color: string; label: string; alertType: 'success' | 'info' | 'warning' | 'error' }
> = {
  ACTIVE: { color: 'green', label: 'Aktif', alertType: 'success' },
  INACTIVE: { color: 'orange', label: 'Pasif', alertType: 'warning' },
  PENDING: { color: 'blue', label: 'Beklemede', alertType: 'info' },
  FAILED: { color: 'red', label: 'Başarısız', alertType: 'error' },
  CANCELED: { color: 'default', label: 'İptal Edildi', alertType: 'warning' },
};

const formatPrice = (price: string, currency: string, period?: string) => {
  const formattedPrice = currency ? `${currency}${price}` : price;
  return period ? `${formattedPrice}${period}` : formattedPrice;
};

const SubscriptionManagementPage: React.FC = () => {
  const navigate = useNavigate();

  const statusConfig = statusConfigMap[mockSubscription.status];

  const handleRetryPayment = () => {
    navigate('/payment/upgrade');
  };

  const handleSelectPackage = (pkg: PackageItem) => {
    navigate(`/payment/upgrade?packageId=${pkg.id}`);
  };

  const handleContactSupport = () => {
    navigate('/result?code=SUPPORT');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f5f5f5',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Space direction="vertical" size={24} style={{ width: '100%' }}>
          <Card style={{ borderRadius: 16 }}>
            
                <Space direction="vertical" size={8}>
                  <Space wrap>
                    <Title level={3} style={{ margin: 0 }}>
                      Abonelik Yönetimi
                    </Title>
                    <Tag color={statusConfig.color}>{statusConfig.label}</Tag>
                  </Space>

                  <Paragraph style={{ marginBottom: 0, fontSize: 16 }}>
                    {mockSubscription.reasonMessage}
                  </Paragraph>

                  <Text type="secondary">
                    Ücretli modüllere erişim abonelik durumunuza göre yönetilir.
                    Aşağıdan mevcut paketinizi inceleyebilir, ödeme işlemini yeniden
                    başlatabilir veya farklı paket seçeneklerini değerlendirebilirsiniz. Sorunun ödeme sağlayıcısı, abonelik durumu veya paket geçişi ile ilgili olduğunu düşünüyorsanız destek ekibimizle iletişime geçebilirsiniz.
                  </Text>
                </Space>
          </Card>

          <Alert
            type={statusConfig.alertType}
            showIcon
            icon={<ExclamationCircleOutlined />}
            message="Bu durum ne anlama geliyor?"
            description="Ödeme başarısız olmuş, abonelik süresi dolmuş, paket iptal edilmiş ya da ödeme adımları tamamlanmamış olabilir. Yetkili kullanıcı olarak abonelik durumunuzu buradan kontrol ederek işlemi yeniden başlatabilirsiniz."
            style={{ borderRadius: 16 }}
          />

          <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}>
              <Card title="Mevcut abonelik bilgisi" style={{ borderRadius: 16, height: '100%' }}>
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="Ürün">
                    {mockSubscription.currentProductName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Paket">
                    {mockSubscription.currentPackageName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ücret">
                    {formatPrice(mockSubscription.price, mockSubscription.currency)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Dönem">
                    {mockSubscription.billingPeriod}
                  </Descriptions.Item>
                  <Descriptions.Item label="Başlangıç Tarihi">
                    {mockSubscription.startedAt}
                  </Descriptions.Item>
                  <Descriptions.Item label="Sonraki Yenileme">
                    {mockSubscription.nextBillingDate}
                  </Descriptions.Item>
                  <Descriptions.Item label="Durum">
                    <Tag color={statusConfig.color}>{statusConfig.label}</Tag>
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card title="Ne yapabilirsiniz?" style={{ borderRadius: 16, height: '100%' }}>
                <Space direction="vertical" size={12}>
                  <Text>• Abonelik ve ödeme durumunuzu kontrol edin.</Text>
                  <Text>• Gerekirse ödeme işlemini yeniden başlatın.</Text>
                  <Text>• Farklı bir paket seçmek istiyorsanız aşağıdaki planları inceleyin.</Text>
                  <Text>• Sorun devam ederse destek ekibiyle iletişime geçin.</Text>
                </Space>

                <Divider />

                <Space wrap>
                  {mockSubscription.paymentRetryAllowed && (
                    <Button
                      type="primary"
                      icon={<ReloadOutlined />}
                      onClick={handleRetryPayment}
                    >
                      Ödemeyi yeniden başlat
                    </Button>
                  )}

                  <Button icon={<CustomerServiceOutlined />} onClick={handleContactSupport}>
                    Destek ile iletişime geç
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
          
          {!mockSubscription.paymentRetryAllowed && (
          <Card title="Paket seçenekleri" style={{ borderRadius: 16 }}>
            <Row gutter={[16, 16]}>
              {mockPackages.map((pkg) => (
                <Col xs={24} md={12} xl={8} key={pkg.id}>
                  <Card
                    style={{
                      borderRadius: 16,
                      height: '100%',
                      border: pkg.popular ? '1px solid #1677ff' : undefined,
                    }}
                  >
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                      <Space wrap>
                        <Title level={4} style={{ margin: 0 }}>
                          {pkg.name}
                        </Title>
                        {pkg.popular ? <Tag color="blue">Önerilen</Tag> : null}
                      </Space>

                      <Title level={3} style={{ margin: 0 }}>
                        {formatPrice(pkg.price, pkg.currency, pkg.period)}
                      </Title>

                      <Divider style={{ margin: '8px 0' }} />

                      <Space direction="vertical" size={8}>
                        {pkg.features.map((feature) => (
                          <Text key={feature}>{`• ${feature}`}</Text>
                        ))}
                      </Space>

                      <div style={{ paddingTop: 8 }}>
                        <Button
                          type={pkg.popular ? 'primary' : 'default'}
                          icon={<RocketOutlined />}
                          block
                          onClick={() => handleSelectPackage(pkg)}
                        >
                          Paketi seç
                        </Button>
                      </div>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
          )}

        </Space>
      </div>
    </div>
  );
};

export default SubscriptionManagementPage;